import CancelListing from '@/components/buttons/CancelListing';
import EditListing from '@/components/buttons/EditListing';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useUserListings } from '@/hooks/use-user-listings';
import { useAuthentication } from '@/hooks/useAuthentication';
import { routes } from '@/utils/routes';
import { Box, Button, Divider, Flex, Text } from '@chakra-ui/react';
import { compareAddresses, formatAddress, formatTokenAmount } from '@onbeam/utils';
import { ListedIcon } from '@sphere/icons';
import { TableRowMediaItem } from '@sphere/ui';
import { formatDistance, parseISO } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { UsdPrice } from '../../../shared/UsdPrice/UsdPrice';

export const AccountListingItemMobile = ({
  listing,
  refetch,
}: {
  listing: ReturnType<typeof useUserListings>['data'][number];
  refetch: () => void;
}) => {
  const { t } = useTranslation('account');
  const marketplaceChain = useMarketplaceChain();
  const { userAddress } = useAuthentication();

  const isOwner = compareAddresses(userAddress, listing.maker);
  const collectionId = listing.criteria?.data?.collection?.id || listing.contract || '';

  return (
    <Box bg="gray.2" p="space.16" rounded="radius.8" mb="space.8">
      <Flex align="flex-start" justify="space-between" mb="space.16">
        <TableRowMediaItem
          href={routes.nft.detail(
            collectionId,
            listing.criteria?.data?.token?.tokenId ?? '',
            marketplaceChain.routePrefix,
          )}
          title={
            listing.criteria?.data?.token?.name || `#${listing.criteria?.data?.token?.tokenId}`
          }
          imageSrc={
            listing.criteria?.data?.token?.image || listing.criteria?.data?.collection?.image
          }
          subtitle={
            <Text fontSize="sm" color="gray.5" noOfLines={1} wordBreak="break-all">
              {formatAddress(listing.criteria?.data?.collection?.name)}
            </Text>
          }
          quantity={listing.quantityRemaining}
          {...(!listing.criteria?.data?.token?.image &&
            listing.criteria?.data?.collection?.image && { rounded: 'full', coverImage: true })}
        />
        {isOwner && (
          <Flex gap="space.4" direction={['column', 'row']} align="end" ml="space.12">
            {/* Only Oracle listings can be edited */}
            {listing.isNativeOffChainCancellable && (
              <EditListing
                listingId={listing.id}
                tokenId={listing.criteria?.data?.token?.tokenId}
                collectionId={collectionId}
                mutate={refetch}
                trigger={
                  <Button
                    variant="secondary"
                    py="space.4"
                    px="space.16"
                    fontSize="xs"
                    lineHeight="3xl"
                    flexShrink={0}
                    w={rem(75)}
                  >
                    {t('listings.table.item.edit')}
                  </Button>
                }
              />
            )}
            <CancelListing
              listingId={listing.id}
              mutate={refetch}
              trigger={
                <Button
                  variant="secondary"
                  py="space.4"
                  px="space.16"
                  fontSize="xs"
                  lineHeight="3xl"
                  flexShrink={0}
                  w={rem(75)}
                >
                  {t('listings.table.item.cancel')}
                </Button>
              }
            />
          </Flex>
        )}
      </Flex>
      <Flex justify="space-between">
        <Text fontSize="xs">
          {`${t('listings.table.item.created')} ${formatDistance(
            parseISO(listing.createdAt),
            new Date(),
            { addSuffix: true },
          )}`}
        </Text>
        {!!listing.validUntil && (
          <Text fontSize="xs">
            {`${t('listings.table.item.expires')} ${formatDistance(
              parseISO(new Date(listing.validUntil * 1000).toISOString()),
              new Date(),
              { addSuffix: true },
            )}`}
          </Text>
        )}
      </Flex>
      <Divider my="space.10" />
      <Flex justify="space-between">
        <Flex align="center" fontSize="xs" gap="space.8">
          <ListedIcon boxSize={rem(16)} />
          {t('listings.table.type.fixed-price')}
        </Flex>
        {listing.price?.amount?.decimal && listing.price?.currency?.symbol && (
          <Flex gap="space.4" fontSize="xs" lineHeight="3xl">
            <Text fontWeight="bold">
              {formatTokenAmount(listing.price?.amount?.decimal, {
                symbol: listing.price?.currency?.symbol,
              })}
            </Text>
            {listing.price.amount.usd && (
              <UsdPrice
                usdValue={listing.price.amount.usd}
                isExact
                color="gray.1"
                prefix="("
                suffix=")"
              />
            )}
            {(listing.quantityRemaining || 0) > 1 && t('listings.labels.each')}
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
